<template>
  <div class="section">
    <div class="toolbar">
      <select v-model="scopeFilter" class="select">
        <option value="">Все области</option>
        <option value="group">Группа</option>
        <option value="contact">Контакт</option>
      </select>
      <div class="toolbar-actions">
        <button class="btn-secondary" :disabled="loading" @click="loadAll">
          {{ loading ? 'Обновляем...' : 'Обновить' }}
        </button>
        <button class="btn-primary" :disabled="loading" @click="startCreate">+ Добавить</button>
      </div>
    </div>

    <div v-if="hasError" class="error-message">Не удалось загрузить привязки. Проверьте подключение к ati-publisher.</div>

    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Из</th>
            <th>В</th>
            <th>Область</th>
            <th>Группа / Контакт</th>
            <th class="col-bool">Активна</th>
            <th class="col-actions">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && items.length === 0">
            <td :colspan="7" class="empty-cell">Загрузка...</td>
          </tr>
          <tr v-else-if="filteredItems.length === 0">
            <td :colspan="7" class="empty-cell">Привязок не найдено</td>
          </tr>
          <tr v-for="item in filteredItems" :key="item.binding_id">
            <td class="mono">{{ item.binding_id }}</td>
            <td>
              <div class="city-cell">
                <strong>{{ cityName(item.from_city_id) }}</strong>
                <small v-if="cityRegion(item.from_city_id)">{{ cityRegion(item.from_city_id) }}</small>
                <small class="mono">city_id: {{ item.from_city_id }}</small>
              </div>
            </td>
            <td>
              <div class="city-cell">
                <strong>{{ cityName(item.to_city_id) }}</strong>
                <small v-if="cityRegion(item.to_city_id)">{{ cityRegion(item.to_city_id) }}</small>
                <small class="mono">city_id: {{ item.to_city_id }}</small>
              </div>
            </td>
            <td>{{ scopeLabel(item.scope) }}</td>
            <td class="mono">{{ item.scope === 'group' ? item.group_id : item.contact_id }}</td>
            <td class="col-bool">
              <span class="badge" :class="item.is_active ? 'badge-on' : 'badge-off'">
                {{ item.is_active ? 'да' : 'нет' }}
              </span>
            </td>
            <td class="col-actions">
              <button
                class="btn-edit"
                :disabled="saving || duplicating || item.scope !== 'group'"
                :title="item.scope !== 'group' ? 'Contact-привязки доступны только для просмотра и удаления' : ''"
                @click="startEdit(item)"
              >Изменить</button>
              <button
                class="btn-copy"
                :disabled="saving || duplicating || item.scope !== 'group'"
                :title="item.scope !== 'group' ? 'Создавать новые contact-привязки нельзя' : ''"
                @click="openDupBinding(item)"
              >Дублировать</button>
              <button class="btn-delete" :disabled="saving || duplicating" @click="removeBinding(item)">
                {{ deletingId === item.binding_id ? '...' : 'Удалить' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <h2>{{ editingId ? 'Редактировать привязку' : 'Новая привязка' }}</h2>
        <div v-if="formError" class="error-message">{{ formError }}</div>
        <div class="field-row">
          <label class="field">
            <span>Из *</span>
            <city-picker
              :model-value="form.from_city_id"
              placeholder="Город отправления"
              @update:model-value="(v) => (form.from_city_id = v)"
            />
          </label>
          <label class="field">
            <span>В *</span>
            <city-picker
              :model-value="form.to_city_id"
              placeholder="Город назначения"
              @update:model-value="(v) => (form.to_city_id = v)"
            />
          </label>
        </div>
        <label class="field">
          <span>Группа *</span>
          <select v-model="form.group_id" class="modal-input">
            <option value="">— выберите группу —</option>
            <option v-for="g in groups" :key="g.group_id" :value="g.group_id">
              {{ g.name }} ({{ g.group_id }})
            </option>
          </select>
        </label>
        <label class="field-inline">
          <input type="checkbox" v-model="form.is_active">
          <span>Активна</span>
        </label>
        <div class="modal-actions">
          <button class="btn-cancel" :disabled="saving" @click="closeModal">Отмена</button>
          <button class="btn-save" :disabled="saving" @click="save">
            {{ saving ? '...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="dupSource" class="modal-overlay" @click.self="closeDupBinding">
      <div class="modal">
        <h2>Дублировать привязку</h2>
        <p class="modal-hint">
          Направление <b>{{ cityName(dupSource.from_city_id) }} → {{ cityName(dupSource.to_city_id) }}</b>
          → новая группа:
        </p>
        <label class="field">
          <span>Целевая группа *</span>
          <select v-model="dupTargetGroupId" class="modal-input">
            <option value="">— выберите группу —</option>
            <option v-for="g in groups" :key="g.group_id" :value="g.group_id">
              {{ g.name }} ({{ g.group_id }})
            </option>
          </select>
        </label>
        <div v-if="dupResult" class="info-message">{{ dupResult }}</div>
        <div class="modal-actions">
          <button class="btn-cancel" :disabled="duplicating" @click="closeDupBinding">Закрыть</button>
          <button class="btn-save" :disabled="duplicating" @click="confirmDupBinding">
            {{ duplicating ? '...' : 'Создать копию' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { deleteBinding, getBindings, getCitiesByIds, getGroups, upsertBinding } from '@/backend/distributorRoutingApi'
import CityPicker from './CityPicker.vue'

export default {
  name: 'BindingsSection',
  components: { CityPicker },
  props: {
    prefill: { type: Object, default: null },
  },
  setup(props) {
    const items = ref([])
    const groups = ref([])
    const citiesById = ref(new Map())
    const loading = ref(false)
    const saving = ref(false)
    const hasError = ref(false)
    const scopeFilter = ref('')
    const showModal = ref(false)
    const editingId = ref(null)
    const deletingId = ref(null)
    const formError = ref('')
    const form = ref(emptyForm())

    function emptyForm() {
      return {
        binding_id: '',
        from_city_id: null,
        to_city_id: null,
        scope: 'group',
        group_id: '',
        is_active: true,
      }
    }

    const scopeLabel = (scope) => (scope === 'group' ? 'Группа' : scope === 'contact' ? 'Контакт' : scope)

    const cityById = (cityId) => citiesById.value.get(Number(cityId))
    const cityName = (cityId) => cityById(cityId)?.city_name || `ID ${cityId}`
    const cityRegion = (cityId) => cityById(cityId)?.region_name || ''

    const loadCityNames = async (bindings) => {
      const cityIds = [...new Set(bindings.flatMap((binding) => [
        Number(binding.from_city_id),
        Number(binding.to_city_id),
      ]).filter((cityId) => Number.isFinite(cityId) && cityId > 0))]
      if (cityIds.length === 0) {
        citiesById.value = new Map()
        return
      }
      try {
        const cities = await getCitiesByIds(cityIds)
        citiesById.value = new Map((Array.isArray(cities) ? cities : []).map((city) => [Number(city.city_id), city]))
      } catch {
        citiesById.value = new Map()
      }
    }

    watch(
      () => props.prefill,
      (p) => {
        if (!p || !p.from_city_id) return
        editingId.value = null
        form.value = {
          binding_id: '',
          from_city_id: p.from_city_id,
          to_city_id: p.to_city_id,
          scope: 'group',
          group_id: '',
          is_active: true,
        }
        formError.value = ''
        showModal.value = true
      },
    )

    const filteredItems = computed(() => {
      if (!scopeFilter.value) return items.value
      return items.value.filter((b) => b.scope === scopeFilter.value)
    })

    const loadAll = async () => {
      loading.value = true
      try {
        const [bindingsData, groupsData] = await Promise.all([
          getBindings({ active_only: false }),
          getGroups({ active_only: false }).catch(() => []),
        ])
        items.value = Array.isArray(bindingsData) ? bindingsData : []
        groups.value = Array.isArray(groupsData) ? groupsData : []
        await loadCityNames(items.value)
        hasError.value = false
      } catch {
        hasError.value = true
      } finally {
        loading.value = false
      }
    }

    const buildBindingId = () => {
      const f = form.value
      if (!f.from_city_id || !f.to_city_id) return ''
      return `b-${f.from_city_id}-${f.to_city_id}-${f.group_id}`
    }

    const startCreate = () => {
      editingId.value = null
      form.value = emptyForm()
      formError.value = ''
      showModal.value = true
    }

    const startEdit = (item) => {
      if (item.scope !== 'group') return
      editingId.value = item.binding_id
      form.value = {
        binding_id: item.binding_id,
        from_city_id: item.from_city_id,
        to_city_id: item.to_city_id,
        scope: 'group',
        group_id: item.group_id || '',
        is_active: item.is_active,
      }
      formError.value = ''
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      editingId.value = null
      formError.value = ''
    }

    const validate = () => {
      const f = form.value
      if (!f.from_city_id || !f.to_city_id) return 'Укажите from_city_id и to_city_id'
      if (!f.group_id) return 'Выберите группу'
      return ''
    }

    const save = async () => {
      formError.value = validate()
      if (formError.value) return
      const f = form.value
      const payload = {
        binding_id: f.binding_id || buildBindingId(),
        from_city_id: Number(f.from_city_id),
        to_city_id: Number(f.to_city_id),
        scope: 'group',
        group_id: f.group_id,
        contact_id: null,
        priority: 0,
        is_active: f.is_active,
      }
      saving.value = true
      try {
        const saved = await upsertBinding(payload)
        const idx = items.value.findIndex((b) => b.binding_id === saved.binding_id)
        if (idx !== -1) items.value.splice(idx, 1, saved)
        else items.value.unshift(saved)
        closeModal()
      } catch (error) {
        formError.value = extractError(error, 'Не удалось сохранить привязку')
      } finally {
        saving.value = false
      }
    }

    const removeBinding = async (item) => {
      if (!window.confirm(`Удалить привязку «${item.binding_id}»?`)) return
      deletingId.value = item.binding_id
      try {
        await deleteBinding(item.binding_id)
        items.value = items.value.filter((b) => b.binding_id !== item.binding_id)
      } catch {
        hasError.value = true
      } finally {
        deletingId.value = null
      }
    }

    const extractError = (error, fallback) => {
      if (typeof error.response?.data?.detail === 'string') return error.response.data.detail
      if (error.response?.data?.detail) return JSON.stringify(error.response.data.detail)
      return fallback
    }

    onMounted(loadAll)

    const dupSource = ref(null)
    const dupTargetGroupId = ref('')
    const duplicating = ref(false)
    const dupResult = ref('')

    const openDupBinding = (item) => {
      if (item.scope !== 'group') return
      dupSource.value = item
      dupTargetGroupId.value = ''
      dupResult.value = ''
    }

    const closeDupBinding = () => {
      dupSource.value = null
      dupTargetGroupId.value = ''
      dupResult.value = ''
    }

    const confirmDupBinding = async () => {
      const src = dupSource.value
      if (!src) return
      if (src.scope !== 'group') return
      if (!dupTargetGroupId.value) { dupResult.value = 'Выберите целевую группу'; return }
      const target = dupTargetGroupId.value
      const payload = {
        binding_id: `b-${src.from_city_id}-${src.to_city_id}-${target}`,
        from_city_id: src.from_city_id,
        to_city_id: src.to_city_id,
        scope: 'group',
        group_id: dupTargetGroupId.value,
        contact_id: null,
        priority: 0,
        is_active: src.is_active ?? true,
      }
      duplicating.value = true
      dupResult.value = ''
      try {
        await upsertBinding(payload)
        await loadAll()
        dupResult.value = 'Создана копия привязки.'
        dupSource.value = null
      } catch (error) {
        dupResult.value = extractError(error, 'Не удалось создать копию')
      } finally {
        duplicating.value = false
      }
    }

    return {
      items, filteredItems, groups, loading, saving, hasError, scopeFilter,
      showModal, editingId, deletingId, formError, form,
      scopeLabel, cityName, cityRegion,
      loadAll, startCreate, startEdit, closeModal, save, removeBinding,
      dupSource, dupTargetGroupId, duplicating, dupResult,
      openDupBinding, closeDupBinding, confirmDupBinding,
    }
  },
}
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.toolbar-actions { display: flex; gap: 8px; }
.select, .modal-input { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; box-sizing: border-box; font-size: 13px; height: 36px; padding: 8px 10px; }
.table-wrap { overflow-x: auto; }
.data-table { border-collapse: collapse; width: 100%; font-size: 13px; min-width: 800px; }
.data-table th, .data-table td { border-bottom: 1px solid #edf0f3; padding: 8px 10px; text-align: left; vertical-align: middle; }
.data-table th { background: #f8fafc; color: #475569; font-size: 11px; font-weight: 800; white-space: nowrap; }
.data-table tbody tr:hover { background: #eef4ff; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.city-cell { display: flex; flex-direction: column; min-width: 150px; }
.city-cell strong { color: #1f2937; font-size: 13px; }
.city-cell small { color: #64748b; font-size: 10px; }
.col-bool { text-align: center; }
.data-table th.col-actions, .data-table td.col-actions { text-align: right; white-space: nowrap; }
.empty-cell { text-align: center !important; color: #6b7280; padding: 24px !important; }
.badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.badge-on { background: #e6f4ea; color: #1e7e34; }
.badge-off { background: #f1f5f9; color: #6b7280; }
.error-message { background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; color: #991b1b; font-size: 12px; margin-bottom: 12px; padding: 10px 12px; }
.info-message { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 10px; color: #1d4ed8; font-size: 12px; margin: 10px 0; padding: 10px 12px; }
.modal-hint { color: #475569; font-size: 13px; margin: 0 0 14px; line-height: 1.5; }
button { border: none; border-radius: 8px; box-sizing: border-box; cursor: pointer; font-weight: 700; font-size: 12px; height: 36px; padding: 0 14px; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.btn-primary { background: #2563eb; color: #fff; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.col-actions button { height: 30px; padding: 0 10px; }
.col-actions button + button { margin-left: 6px; }
.btn-edit { background: #eef4ff; color: #1d4ed8; }
.btn-copy { background: #f0fdf4; color: #166534; }
.btn-save { background: #16a34a; color: #fff; }
.btn-cancel { background: #f1f5f9; color: #475569; }
.btn-delete { background: #fee2e2; color: #991b1b; }
.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; border-radius: 14px; box-shadow: 0 20px 50px rgba(15,23,42,0.25); padding: 22px; width: 460px; max-width: 92vw; }
.modal h2 { margin: 0 0 14px; font-size: 18px; }
.field-row { display: flex; gap: 12px; }
.field-row .field { flex: 1; }
.field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 12px; color: #475569; }
.field-inline { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 13px; color: #1f2937; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
</style>
